import React, { useState } from 'react';
import { Review } from '../../types/book';
import { reviewService } from '../../services/reviewService';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { PencilIcon, TrashIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import ReviewForm from './ReviewForm';

interface ReviewListProps {
  reviews: Review[];
  currentUserId: string | null;
  bookId: string;
  onReviewUpdated: () => void;
}

const ReviewList: React.FC<ReviewListProps> = ({
  reviews,
  currentUserId,
  bookId,
  onReviewUpdated
}) => {
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  const handleDeleteReview = async (reviewId: string) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        setLoading(reviewId);
        await reviewService.deleteReview(reviewId);
        onReviewUpdated();
      } catch (error) {
        console.error('Error deleting review:', error);
        alert('Failed to delete review. Please try again.');
      } finally {
        setLoading(null);
      }
    }
  };

  const handleEditReview = (review: Review) => {
    setEditingReview(review);
  };

  const handleReviewSubmitted = () => {
    setEditingReview(null);
    onReviewUpdated();
  };

  const handleCancelEdit = () => {
    setEditingReview(null);
  };

  const renderStars = (rating: number) => {
    const stars: JSX.Element[] = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <StarIconSolid
          key={i}
          className={`h-4 w-4 ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
        />
      );
    }
    return stars;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <StarIconSolid className="mx-auto h-12 w-12 text-gray-400" />
        <h4 className="mt-2 text-sm font-medium text-gray-900">No reviews yet</h4>
        <p className="mt-1 text-sm text-gray-500">
          Be the first to review this book.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {editingReview && (
        <ReviewForm
          bookId={bookId}
          userId={currentUserId!}
          existingReview={editingReview}
          onReviewSubmitted={handleReviewSubmitted}
          onCancel={handleCancelEdit}
        />
      )}
      
      {reviews.map((review) => (
        <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-10 w-10 text-gray-400" />
              <div>
                <h5 className="text-sm font-medium text-gray-900">
                  {review.userDisplayName || 'Anonymous User'}
                </h5>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="flex">
                    {renderStars(review.rating)}
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDate(review.timestamp)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Edit/Delete buttons for review owner */}
            {currentUserId === review.userId && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditReview(review)}
                  disabled={editingReview?.id === review.id}
                  className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md disabled:opacity-50"
                  title="Edit review"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteReview(review.id)}
                  disabled={loading === review.id}
                  className="p-2 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-md disabled:opacity-50"
                  title="Delete review"
                >
                  {loading === review.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <TrashIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            )}
          </div>
          
          <div className="mt-4">
            <p className="text-gray-700 whitespace-pre-wrap">{review.comment}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReviewList; 